import type { Diagnostic } from '../edf/errors.js';
import type { EdfFile } from '../edf/reader.js';
import { counted, grouped, listed } from '../format/list.js';
import { plainSeconds } from '../format/number.js';

export interface AnnotationTimingData {
  recordStarts: (number | null)[];
  malformed: number;
  /** Unreadable TALs in first position, which carry timing rather than an event. */
  malformedTimekeeping?: number;
  malformedTimekeepingWithText?: number;
}

/**
 * The EDF+D warning's promise, withdrawn when the file cannot keep it.
 *
 * The header parser raises DISCONTINUOUS with the hint "Each row carries its true recording
 * time, so gaps stay visible instead of being closed" — which is what an EDF+D conversion
 * does, when the record times can be read. When they cannot, the very next warning in the
 * same run says the opposite: "Times are written as if the records were contiguous. Any gaps
 * are lost." Two warnings, printed together, and the second denies the first.
 *
 * The parser cannot know: whether the starts can be derived is settled here, after the
 * annotation channel has been read. So the hint is amended where the answer is, the same way
 * `withoutFileRateWarning` drops a header diagnostic the plan has superseded.
 */
export function withTimingPromiseKept(
  diagnostics: readonly Diagnostic[],
  derived: boolean,
): Diagnostic[] {
  if (derived) return [...diagnostics];
  return diagnostics.map((d) =>
    d.code === 'DISCONTINUOUS' && d.hint?.includes('gaps stay visible')
      ? {
          ...d,
          hint:
            'Where its records sit in time is not recorded in this file, so they are written ' +
            'as if contiguous — see the warning below.',
        }
      : d,
  );
}

/**
 * Resolve the true start time of every data record.
 *
 * Continuous recordings need no table because their record positions are
 * arithmetic. EDF+D recordings carry their positions in the annotation channel;
 * missing or malformed timekeeping entries are reported before falling back.
 */
export function deriveRecordStarts(
  file: EdfFile,
  annotationData: AnnotationTimingData,
): { starts: Float64Array | null; diagnostics: Diagnostic[] } {
  const diagnostics: Diagnostic[] = [];

  if (annotationData.malformed > 0) {
    diagnostics.push({
      code: 'ANNOTATION_DECODE_FAILED',
      severity: 'warning',
      // Through `counted`, with the irregular plural spelled out — which is the case its
      // own comment names and which nothing was using it for.
      message:
        `${counted(annotationData.malformed, 'annotation entry', 'annotation entries')} ` +
        `${annotationData.malformed === 1 ? 'was' : 'were'} unreadable and could not be exported.`,
      /*
        "The rest were exported normally" was said whether or not there was a rest.

        A writer that cannot state an onset tends not to manage it anywhere, so a file whose
        every event entry is unreadable is the ordinary way here — and it was answered with
        the count of what was lost and a sentence saying the remainder came through, over an
        annotations.csv holding its header and no rows:

            warning: 2 annotation entries were unreadable and could not be exported.
                     The rest were exported normally. ...

            Wrote scoring_csv
              annotations.csv  0  rows

        Four lines apart, the summary settling it. Same shape as the "No event was lost"
        sentence forty lines down, which was corrected for the same reason: a hint true of
        most files is not true of the one it is printed over.

        Said without counting, rather than by counting. What makes the old sentence wrong is
        that it asserts a remainder exists; what it was for is the assurance that nothing
        readable was dropped, and that holds however many were readable. Counting instead
        would have split `--info` from a conversion — the scan behind `--info` stops at the
        first record that states a time and never counts the events, which is the bound that
        keeps it a header read.
      */
      hint:
        'Every entry that could be read is exported. The file may have been written by a ' +
        'non-conforming tool.',
    });
  }


  /*
    A timekeeping TAL is not an event, and saying it "could not be exported" describes the
    wrong loss twice over.

    These were counted among the annotations, so a file with one unreadable timekeeping TAL
    and three good events announced "1 annotation entry was unreadable and could not be
    exported" — while exporting all three. Nothing was missing from annotations.csv; what
    went missing was a record's position in time, which the message never mentioned.
  */
  const lostTimekeeping = annotationData.malformedTimekeeping ?? 0;
  const withText = annotationData.malformedTimekeepingWithText ?? 0;
  // The EDF+D branch below raises its own, which names the records and is more specific.
  // Saying both would report one problem twice.
  if (lostTimekeeping > 0 && file.header.continuity !== 'EDF+D') {
    const one = lostTimekeeping === 1;
    diagnostics.push({
      code: 'ANNOTATION_DECODE_FAILED',
      severity: 'warning',
      // Through `counted`, like the four other counts of "data record" in this file. This one
      // spelled the noun and the verb by hand — `record${one ? '' : 's'} carr${one ? 'ies' :
      // 'y'}` — while its nearest neighbour, sixty lines down and about the same records,
      // writes `${counted(n, 'data record')} ${one ? 'carries' : 'carry'}`. Same phrase, same
      // file, two spellings, and only one of them can be checked against the helper.
      message:
        `${counted(lostTimekeeping, 'data record')} ${one ? 'carries' : 'carry'} a timekeeping ` +
        `annotation that could not be read, so ${one ? 'it does' : 'they do'} not say where in ` +
        `time ${one ? 'it sits' : 'they sit'}.`,
      hint:
        /*
          "No event was lost" was said whatever the entry held.

          A first-position TAL states the record's start time and may carry events after it,
          which the specification allows and writers do. When one of those cannot be parsed
          the events go with it: a six-event file came out with two, and the only warning
          about it said in so many words that nothing had gone. Those entries are counted
          among the unreadable ones now, which is the warning printed above this one; this
          sentence keeps to what is true of the entries it is actually about.
        */
        /*
          Grouped, like the count of the same records one line above it.

          The message says `counted(lostTimekeeping, 'data record')`; the hint under it counts
          a subset of those records and spelled the number by hand. When the subset is all of
          them — which is the ordinary case, since a writer that cannot state an onset tends
          not to manage it anywhere — the two print the same number two lines apart, one
          grouped and one not:

              warning: 1,010 data records carry a timekeeping annotation that could not be
                       read, so they do not say where in time they sit.
                       1010 of them also carried event text, which went with them and is
                       counted above.
        */
        (withText > 0
          ? `${withText === 1 ? 'One of them' : `${grouped(withText)} of them`} also carried event text, ` +
            `which went with ${withText === 1 ? 'it' : 'them'} and is counted above. A ` +
            `timekeeping annotation itself states a record's start time and is never exported. `
          : 'No event was lost — a timekeeping annotation states a record\'s start time and ' +
            'is never exported. ') + 'Times are derived from the records that could be read.',
    });
  }

  /*
    A continuous recording's records are contiguous, but the first one need not sit at zero.

    EDF+ puts the header's start time and every annotation onset on one origin, and says the
    first data record's timekeeping TAL "always starts with +0.X", stating the fraction of a
    second by which that record follows it. Ignoring that fraction timed the samples from 0
    while the events kept their true onsets, so the two ended up on origins half a second
    apart — an event at +0.75 in a 4 Hz recording whose first TAL reads +0.5 landed on sample
    3 instead of sample 1. The same file marked EDF+D, byte-identical but for the reserved
    field, placed it correctly, which is what gives the omission away.

    Records stay contiguous, which is what continuous means: only the origin moves. A first
    TAL of +0 needs no table at all, and that is nearly every file.
  */
  if (file.header.continuity !== 'EDF+D') {
    if (file.header.continuity !== 'EDF+C') {
      /*
        An annotation channel the reserved field never claimed.

        Without an `EDF+C` or `EDF+D` marker this is a plain EDF file, so the origin is not
        applied and the samples are timed from zero. The annotation channel is found by label
        rather than by the marker, though, so its events are still read and exported — with
        the onsets the file gives them.

        On a file whose timekeeping says the records start at 1000s, that put signals.csv at
        0.000 and the event at 1000.5 in annotations.csv: two files from one conversion, a
        thousand seconds apart, and nothing said so. output-files promises the opposite —
        "`onset_s` is on the same clock as `time_s` in the signal files".

        Reported rather than repaired. Which clock is right is not knowable from here: the
        marker says plain EDF and the annotation channel says otherwise, and picking one would
        move either the samples or the events by the origin on a guess.
      */
      const stated = annotationData.recordStarts.find((start) => start !== null) ?? null;
      const base = file.header.isBdf ? 'BDF' : 'EDF';
      const plus = `${base}+`;
      if (stated !== null && Math.abs(stated) > 0) {
        diagnostics.push({
          code: 'MISSING_EDF_PLUS_MARKER',
          severity: 'warning',
          // The markers as this file spells them, which is what the contradiction warning
          // sixty lines down already does and what NO_SIGNAL_CHANNELS was fixed for. A BDF
          // recording was told to "Mark the file EDF+C" — not a value BDF+ defines — and
          // that it "is read as plain EDF", which it is not.
          message:
            `This file has an annotation channel stating that its records begin at ` +
            `${plainSeconds(stated)}s, but its reserved field carries no ${plus}C or ${plus}D ` +
            `marker — so it is read as plain ${base}, time_s counts from zero, and the two ` +
            `disagree by ${plainSeconds(stated)}s.`,
          hint:
            `annotations.csv keeps the onsets the file gives, so its events and signals.csv ` +
            `are on different clocks. Mark the file ${plus}C, or subtract the offset from ` +
            `the onsets, before joining them.`,
        });
      }
      return { starts: null, diagnostics };
    }

    /*
      The origin comes from whichever record first states one, not from record 0 alone.

      Reading only `recordStarts[0]` meant a single unreadable timekeeping TAL threw the
      origin away and timed the whole file from zero — while records 1 and 2, saying plainly
      that they start at 1.5s and 2.5s, went unread. A recording whose records sit at 0.5s,
      1.5s and 2.5s came out with every sample 0.5s earlier than the file states, against
      annotation onsets that kept their true values. That is precisely the mismatch 0.4.9
      fixed, arriving through the one hole left in it, and the byte-identical EDF+D twin
      timed it correctly, which is what gives it away.

      Continuity is what makes this recoverable: record i sits at `origin + i * duration`,
      so any readable record determines the origin for all of them.
    */
    const origin = originOf(annotationData.recordStarts, file.header.recordDuration);
    if (origin === null) return { starts: null, diagnostics };

    const contiguous = new Float64Array(file.recordCount);
    for (let i = 0; i < file.recordCount; i++) {
      contiguous[i] = origin + i * file.header.recordDuration;
    }

    /*
      A file marked continuous whose own records disagree about it.

      Nothing looked at records past the first, so an EDF+C file whose records are in fact
      spread out was timed as though they were contiguous and said nothing. The records are
      being read here anyway, so the contradiction costs nothing to notice — and it is the
      file, not the reader, that has to be wrong for this to fire.

      Compared against what the file can express, not for equality. 0.4.41 asked whether the
      two doubles were the same, which they are not: a recording of 0.1s records sitting at
      0.1, 0.2, 0.3 ... is contiguous by construction, and 0.1 + 2 * 0.1 is
      0.30000000000000004. Two of its eight records were reported as contradicting
      continuity, on an ordinary file — and under --strict that was a failed run. The
      smallest interval the recording distinguishes is one sample of its fastest channel;
      anything below half of that is arithmetic, not a gap. `canCarry` has already refused
      origins where the double spacing swamps that interval, so the representation error is
      under the tolerance by construction rather than by hope.
    */
    const tolerance = finestInterval(file) / 2;
    const contradicting = annotationData.recordStarts.filter(
      (declared, i) =>
        typeof declared === 'number' && Math.abs(declared - (contiguous[i] as number)) > tolerance,
    ).length;
    if (contradicting > 0) {
      const continuous = file.header.isBdf ? 'BDF+C' : 'EDF+C';
      const discontinuous = file.header.isBdf ? 'BDF+D' : 'EDF+D';
      diagnostics.push({
        code: 'DISCONTINUOUS',
        severity: 'warning',
        /*
          The markers as the file spells them.

          `continuity` normalises `BDF+C` to the internal `EDF+C` tag, and that tag reached
          the message: a BDF+ recording was told it is "marked continuous (EDF+C)" — a string
          it does not contain — and advised that it "should have been marked EDF+D", which is
          not a value BDF+ defines. A reader grepping the header for either finds nothing.

          The sibling discontinuous warning has done this since 0.3.x: `${'$'}{isBdf ? 'BDF+D' :
          'EDF+D'}`. Same code, same header field, and the continuous branch never got it.
        */
        /*
          Both counts grouped, which is the whole point of the sentence: it puts one against
          the other. 0.8.5 gave `counted` the grouping and went through the bare numbers
          standing beside it — including this exact phrase in the ANNOTATION_DECODE_FAILED
          warning eighty lines down, `${grouped(missing.length)} of ${counted(...)}` — and
          missed this one, so a three-thousand-record file read "2999 of its 3,000 data
          records", three words apart.
        */
        message:
          `This file is marked continuous (${continuous}), but ${grouped(contradicting)} of its ` +
          `${counted(file.recordCount, 'data record')} ` +
          `${contradicting === 1 ? 'says it starts' : 'say they start'} somewhere other than ` +
          `where continuity puts ${contradicting === 1 ? 'it' : 'them'}.`,
        hint:
          `Times are written as if the records were contiguous, which is what ${continuous} ` +
          `means. If the recording really has gaps, the file should have been marked ` +
          `${discontinuous}.`,
      });
    }
    const first = origin;
    const last = contiguous[file.recordCount - 1] ?? first;
    if (!canCarry(last, file)) {
      diagnostics.push(unusableOrigin(first, file));
      return { starts: null, diagnostics };
    }
    /*
      An origin of zero is the same as no origin, for timing. It is not the same for the
      check above.

      This returned early on `origin === 0`, which is right about the times — contiguous
      starts from zero are what timing from zero already produces — and skipped the
      contradiction check on the way past. So an EDF+C file whose records say 0, 5 and 10 on
      one-second records went unreported, while the same file shifted one second, saying 1, 6
      and 11, was reported. The contradiction is in records 1 and 2 either way; where record 0
      happens to sit decides nothing about it.
    */
    if (origin === 0) return { starts: null, diagnostics };
    return { starts: contiguous, diagnostics };
  }

  if (file.annotationSignals.length === 0) {
    diagnostics.push({
      code: 'DISCONTINUOUS',
      severity: 'warning',
      message:
        'This file is marked discontinuous but has no annotation channel, so where its ' +
        'records sit in time is not recorded anywhere.',
      hint: 'Times are written as if the records were contiguous. Any gaps are lost.',
    });
    return { starts: null, diagnostics };
  }

  /*
    A record with no readable time is placed from the origin the other records establish,
    not from zero.

    `i * recordDuration` assumed the recording began at zero, which is the one thing the
    other records are in a position to contradict: a file starting at 0.5s put its
    unreadable record at 0.000 while its neighbours sat at 1.5s and 2.5s. The guess is still
    a guess — a discontinuous file may have a gap exactly there — and it is still reported
    below, but starting it from where the recording actually begins is strictly closer, and
    it makes an EDF+D file agree with its byte-identical EDF+C twin about record 0.
  */
  const base = originOf(annotationData.recordStarts, file.header.recordDuration) ?? 0;
  const starts = new Float64Array(file.recordCount);
  const missing: number[] = [];
  for (let i = 0; i < file.recordCount; i++) {
    const declared = annotationData.recordStarts[i];
    if (declared === null || declared === undefined) {
      missing.push(i);
      starts[i] = base + i * file.header.recordDuration;
    } else {
      starts[i] = declared;
    }
  }

  if (missing.length > 0) {
    /*
      Cut by the function that cuts every other list in a sentence here.

      This one rolled its own: five items and a bare `…`, where `listed` shows eight and counts
      what it left — "and 32 more" on the rate warning, "and 112 more" on the leftover files.
      The count is the honest half, and 0.7.58 taught the shared one not to hide a single item
      behind a phrase longer than the item. None of that reached this message, because it was
      never asking. Two implementations of one job, differing in the limit and in what they say
      about the tail.
    */
    const shown = listed(missing.map(String));
    // "1 of 3 data records carry ... their true position" — the subject is the one, not the three.
    const one = missing.length === 1;
    diagnostics.push({
      code: 'ANNOTATION_DECODE_FAILED',
      severity: 'warning',
      message:
        `${grouped(missing.length)} of ${counted(file.recordCount, 'data record')} ${one ? 'carries' : 'carry'} no ` +
        `readable timekeeping annotation (record${one ? '' : 's'} ${shown}), so ` +
        `${one ? 'its' : 'their'} true position in time is unknown.`,
      hint: one
        ? 'That record is timed as if it were contiguous; treat its timestamp as unreliable.'
        : 'Those records are timed as if they were contiguous; treat their timestamps as unreliable.',
    });
  }

  /*
    Furthest from zero, in either direction.

    This took the signed maximum and seeded it with 0, so a recording whose records all sit
    at negative onsets never got past the seed: `furthest` stayed 0, which any interval can
    carry. Then the samples collapsed anyway, because the arithmetic that defeats a large
    positive origin defeats a large negative one identically — at -1e16 seconds, adding a
    1-second sample interval leaves the double unchanged.

    A four-record recording of eight samples wrote two rows, exit 0, no warning. Its
    byte-for-byte positive mirror wrote all eight and explained why it had to time them from
    zero. Same file, same failure, opposite sign, opposite outcome — and the silent one is
    the one that loses data, which is exactly what unusableOrigin exists to prevent.
  */
  let furthest = 0;
  for (const start of starts) if (Math.abs(start) > Math.abs(furthest)) furthest = start;
  if (!canCarry(furthest, file)) {
    diagnostics.push(unusableOrigin(furthest, file));
    return { starts: null, diagnostics };
  }

  /*
    Two ways a record can put the time column out of order, and only one was being looked for.

    A record starting before the one before it is the obvious case. The other is a record
    starting before the one before it *ends*: starts of 0, 0.5 and 1.0 on one-second records
    are strictly increasing, so nothing fired, and the rows still came out 0.25, 0.5, 0.75,
    0.5 — because record 0's samples run to 0.75 while record 1 begins at 0.5. Overlapping
    acquisition is what a device does when it re-sends a buffer, and the reader has no more
    to say about it than about the reversed case: every sample is written, in file order,
    with the time the file gives it.

    Contiguity is not overlap. A continuous recording has `starts[i] === starts[i-1] +
    duration` exactly, so the comparison is made strict by a fraction of the finest interval
    the recording can express — the same measure the origin check uses.
  */
  const slack = finestInterval(file) / 2;
  let outOfOrder = 0;
  let overlapping = 0;
  for (let i = 1; i < starts.length; i++) {
    const previous = starts[i - 1] as number;
    const current = starts[i] as number;
    if (current < previous) outOfOrder++;
    else if (current + slack < previous + file.header.recordDuration) overlapping++;
  }
  if (outOfOrder > 0) {
    diagnostics.push({
      code: 'DISCONTINUOUS',
      severity: 'warning',
      message:
        `${counted(outOfOrder, 'data record')} ` +
        `${outOfOrder === 1 ? 'starts' : 'start'} earlier than the record before ` +
        `${outOfOrder === 1 ? 'it' : 'them'}.`,
      hint: 'Rows are written in file order, so the time column will not increase monotonically.',
    });
  }
  if (overlapping > 0) {
    diagnostics.push({
      code: 'DISCONTINUOUS',
      severity: 'warning',
      message:
        `${counted(overlapping, 'data record')} ` +
        `${overlapping === 1 ? 'starts' : 'start'} before the record before ` +
        `${overlapping === 1 ? 'it' : 'them'} ends, so ${overlapping === 1 ? 'its' : 'their'} ` +
        `samples overlap in time.`,
      /*
        An overlap does not always put the column out of order, and this said it did.

        The two counts above share a sentence and only one of them earns it. A record starting
        *earlier* than the one before it steps the column back by construction. A record
        starting before the one before it *ends* need not: what decides is whether it begins
        after the previous record's last sample, and the last sample is one interval short of
        the record's end. Three-tenths of a second of overlap on a one-second record sampled
        twice a second writes

            time_s,A
            0.000
            0.500
            0.700
            1.200

        which increases at every step, under a warning saying it would not. Both records do
        describe 0.700 to 1.000, which is the thing that is true either way and is what the
        sentence says now.
      */
      hint:
        'Rows are written in file order, and two records describe the same stretch of time. ' +
        'Where they overlap by more than one sample interval the time column steps backwards.',
    });
  }

  return { starts, diagnostics };
}

/**
 * The recording's origin, from the first record that states where it is.
 *
 * Records of a continuous recording sit end to end, so record `i` beginning at `t` puts the
 * origin at `t - i * duration`. Any one readable timekeeping TAL is therefore enough, which
 * is what stops one unreadable entry from costing the whole file its position in time.
 */
function originOf(recordStarts: readonly (number | null)[], recordDuration: number): number | null {
  for (const [index, declared] of recordStarts.entries()) {
    if (typeof declared !== 'number') continue;
    const origin = declared - index * recordDuration;
    return Number.isFinite(origin) ? origin : null;
  }
  return null;
}

/**
 * Whether times this far out can still tell one sample from the next.
 *
 * A double spaces its values further apart the larger they get: at 1e16 the gap is 2
 * seconds, so `t + 1` is `t`. Past that point a recording's declared origin stops being a
 * position and becomes a wall — the arithmetic that places records and samples returns the
 * origin itself, whatever is added to it.
 *
 * The finest thing that has to survive is the gap between two consecutive samples of the
 * fastest channel, since that is what the time column is made of. If that survives, so does
 * a whole record.
 */
function canCarry(origin: number, file: EdfFile): boolean {
  if (!Number.isFinite(origin)) return false;
  // Asked of the origin furthest from zero, whichever side it is on: the spacing of doubles
  // grows with magnitude, not with value, so -1e16 and +1e16 fail this identically.
  return origin + finestInterval(file) > origin;
}

/**
 * The shortest span this recording can tell apart: one sample of its fastest channel.
 *
 * The time column is made of these, so nothing below one is a distinction the file is in a
 * position to make — which is what makes it the right size for both the "can this origin
 * still separate two samples" question and the "is this record really somewhere else"
 * question.
 */
function finestInterval(file: EdfFile): number {
  let interval = file.header.recordDuration;
  for (const signal of file.header.signals) {
    if (signal.isAnnotations || !(signal.samplesPerRecord > 0)) continue;
    const step = file.header.recordDuration / signal.samplesPerRecord;
    if (step > 0 && step < interval) interval = step;
  }
  return interval;
}

/**
 * An origin the file's own arithmetic cannot express, reported rather than acted on.
 *
 * Two things went wrong when this was taken at face value, both of them quiet. A file whose
 * records all collapsed onto one instant made the recording zero seconds long, and the
 * window resolver — which had no reason to suspect the recording rather than the request —
 * blamed a flag nobody had passed:
 *
 *     error: --start 100000000000000000s is at or past the end of this
 *            100000000000000000s recording.
 *
 * Slightly below that, the collapse is partial: `records[i].start + recordDuration` equals
 * the start again, so the test for "does this record overlap the window" fails for every
 * record whose neighbour rounded onto it. A twelve-row recording wrote four rows, exit 0,
 * no warning — the eight that vanished looked exactly like a file that never had them.
 *
 * Timing from zero is what the file did before 0.4.9 taught it to honour the first
 * timekeeping TAL, and at this magnitude it is the only column that can hold distinct
 * values. The origin is lost, so this says so.
 */
function unusableOrigin(origin: number, file: EdfFile): Diagnostic {
  /*
    `Infinity` is a token, not a distance.

    `plain` expands exponent notation and hands anything else back, so a non-finite origin
    arrived on screen as "place it Infinitys from its own start date" — the exact form
    `formatDuration` exists to keep off the screen, its comment naming "NaNs" and "Infinitys"
    as what the fallback used to print, and the word the rest of this tool uses for the same
    quantity is "unknown". The origin overflows for the reason the recording's length does:
    a record duration near the top of a double, multiplied out over three records, leaves it.
  */
  const away = Number.isFinite(origin)
    ? `${plainSeconds(origin)}s from its own start date`
    : 'further from its own start date than a number can hold';
  return {
    code: 'DISCONTINUOUS',
    severity: 'warning',
    message:
      `This recording's timekeeping annotations place it ${away}, which is too far out for ` +
      `its ${plainSeconds(file.header.recordDuration)}s records to be told apart: at that magnitude ` +
      `adding a sample interval leaves the number unchanged.`,
    /*
      And where the absolute times actually are, which is not always anywhere.

      "Add the onsets in annotations.csv to recover absolute times if you need them" is the
      whole of the advice, and an onset belongs to an *event*. The recording this is raised
      on has an annotation channel carrying nothing but the timekeeping entries that place
      its records — which is how the origin was read in the first place, and which are never
      exported. So the file it sent the reader to holds one header line:

          warning: This recording's timekeeping annotations place it 100000000000000000s
                   from its own start date ...
                   Sample times are written from zero instead, so every row is present and
                   the column increases. Add the onsets in annotations.csv to recover
                   absolute times if you need them.

          Wrote ./converted
            signals.csv      12  rows
            annotations.csv   0  rows

      Nothing else in the output records the origin either: `time_s` counts from zero,
      `first_sample_seconds` is 0 and `time_span_seconds` is the nominal length, so on a
      recording with no events this warning is the only trace of it. The advice now says
      which onsets those are and that a recording without events has none of them.
    */
    hint:
      'Sample times are written from zero instead, so every row is present and the column ' +
      "increases. An event's own onset is on the original clock, so the onsets in " +
      'annotations.csv recover it for a recording that carries events — the timekeeping ' +
      'entries that place the records are never exported.',
  };
}
